import {Button, Icon, ListItem, Modal, Text} from '@mc/ui-v4';
import React, {MutableRefObject, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {mcMoveLeft, mcSearch} from '@mc/react-ui-icons';
import {UseQueryResult} from 'react-query';
import {EnumTypVysledek, FeaturePointerVO, LayerVO} from '../../api';
import {useMapioContext} from '../../mapioContext';
import {FeatureFilter, FeatureZoom, GroupFeaturePointer, LayerId} from '@mc/react-mapio';
import {useLayersConfig} from '../../hooks/useLayersConfig';
import {useIsMobile} from '../../hooks/useIsMobile';
import {useAppConfig} from '@mc/react-app-base';
import {actionApiCall} from '@mc/react-app-v4';
import {SidebarContent} from '../sidebar/SidebarContent';
import {LegendModal} from '../modals/LegendModal';
import {ApiCallWaitingModal} from '../modals/ApiCallWaitingModal';
import {preloadLegendData} from '../../helpers/LegendUtils';
import {SearchPanelInput} from './SearchPanelInput';
import {SearchResultRow} from './SearchResultRow';
import {ViewportResultItem} from './ViewportResultItem';
import {ViewportVirtualList} from './ViewportVirtualList';
import {useSearchPanelSearch} from './useSearchPanelSearch';
import {useViewportFeatures} from './useViewportFeatures';
import {useSearchPanelExtent} from './useSearchPanelExtent';
import {useSearchResultCards} from './useSearchResultCards';
import {getFoundCountLabel, SearchListItem, ViewportResult} from './searchPanelTypes';
import {BottomSheet} from '../mobile/BottomSheet';
import {FilterTreePanel} from './filter-tree/FilterTreePanel';
import {FilterTagsBar} from './filter-tree/FilterTagsBar';
import {attachLayerIcons, buildCatalogFilterNodes, useSearchFilterTree} from './filter-tree/useSearchFilterTree';
import {useCatalogLayers} from '../../hooks/useCatalogLayers';
import {useFilterTree} from './filter-tree/useFilterTree';
import {buildAppliedFilter, collectLayerTargetIds} from './filter-tree/filterTargets';
import {getLabelLayerIds} from '../../helpers/LayerConfigUtils';
import './SearchPanel.scss';

type Props = {
    /** Zda je panel aktuálně zobrazený obsah sidebaru. Zůstává namountovaný i když ne (kvůli zachování stavu hledání). */
    active: boolean;
    onItemSelected: (backToListFn: () => void) => void;
    onClose: () => void;
    /** Pointery objektů ve výřezu (s layerId) – event EXTENT_FEATURES, režim "všechny zapnuté vrstvy". */
    extentFeaturePointers?: GroupFeaturePointer[];
    /** Sem panel zaregistruje funkci pro re-aktivaci extent filtru; MapPage ji volá při pohybu mapou. */
    extentRefreshRef: MutableRefObject<(() => void) | null>;
    /** Aktuální (datumový) filtr mapy – při aplikaci stromového filtru se s ním mergují layerConditions. */
    dateFilterRange?: FeatureFilter | null;
};

const VIEWPORT_ROW_HEIGHT = 80;

export const SearchPanel: React.FC<Props> = ({active, onItemSelected, onClose, extentFeaturePointers, extentRefreshRef, dateFilterRange}) => {
    const mapioContext = useMapioContext();
    const isMobile = useIsMobile();
    const {topic, topicId, mapRef, disabled, activeLayers} = mapioContext.value;
    const {setSelectedDetailFeature, setActiveLayers} = mapioContext;
    const layersConfigQuery: UseQueryResult<{[key: string]: LayerVO}> = useLayersConfig();
    // @ts-ignore
    const {serverContextPath, serverPublic} = useAppConfig();

    // Mobilní zjednodušené veřejné Mapio: panel hledání je trvalý bottom sheet místo fullscreen karty.
    const mobileSheet = isMobile && serverPublic;
    // Signál pro sbalení sheetu na peek (po kliku na lokaci/ulici) + signál pro vytažení (focus hledání)
    // + zda je sheet vytažen úplně nahoru (kvůli legendě).
    const [collapseSignal, setCollapseSignal] = useState(0);
    const [expandSignal, setExpandSignal] = useState(0);
    const [sheetAtMax, setSheetAtMax] = useState(false);
    const MAX_SNAP_VH = 92;
    // Snap body sheetu: peek 12 vh (výchozí, jen vyhledávací pole) → 32 → 50 vh po focusu pole → 92 vh (max, legenda).
    const SHEET_SNAP_POINTS = [14, 50, MAX_SNAP_VH];
    // Index snap bodu 50 vh (cíl při focusu vyhledávacího pole).
    const SHEET_SEARCH_SNAP_INDEX = 2;
    // Index snap bodu 32 vh (cíl po návratu ze zavřeného detailu).
    const SHEET_BACK_SNAP_INDEX = 1;

    // Cílový index při vytažení sheetu (expandSignal). Focus pole → 50 vh, návrat z detailu → 32 vh.
    const [expandTargetIndex, setExpandTargetIndex] = useState(SHEET_SEARCH_SNAP_INDEX);

    // Návrat do panelu po zavření detailu objektu (křížek / klik do mapy): panel se znovu zobrazí
    // (active false→true) a chceme ho mít na 32 vh, ne ve výchozím peeku (12 vh). Úvodní mount
    // ignorujeme (prev = active), aby první zobrazení zůstalo na výchozím peeku.
    const prevActiveRef = useRef(active);
    useEffect(() => {
        const wasActive = prevActiveRef.current;
        prevActiveRef.current = active;
        if (mobileSheet && !wasActive && active) {
            setExpandTargetIndex(SHEET_BACK_SNAP_INDEX);
            setExpandSignal(s => s + 1);
        }
    }, [active, mobileSheet]);

    // Režim "Nastavení filtru" – stromový výběr hodnot číselníku (zatím mock, bez API).
    const [filterMode, setFilterMode] = useState(false);
    // Snímek zaškrtnutí při otevření filtru – pro „zrušit změny" (křížek) obnovíme tento stav.
    const filterSnapshotRef = useRef<Set<number> | null>(null);

    // Data stromu filtru (zatím mock; server viz useSearchFilterTree) + jeho stav. Stav držíme ZDE (ne ve
    // FilterTreePanelu), aby výběr přežil přepnutí "Zpět na seznam" a filtr dál platil i při procházení seznamu.
    const rawFilterTreeNodes = useSearchFilterTree(topicId, activeLayers, mapioContext.value.context);
    const catalogLayersQuery = useCatalogLayers(topicId);
    // Dimenze VRSTEV z katalogu (všechny vrstvy tématu v pořadí katalogu) + atributová dimenze ze serveru;
    // u LAYER uzlů doplníme ikonu a barvu vrstvy z konfigurace.
    const filterTreeNodes = useMemo(
        () => attachLayerIcons([...buildCatalogFilterNodes(catalogLayersQuery.data), ...rawFilterTreeNodes], layersConfigQuery.data, serverContextPath),
        [rawFilterTreeNodes, layersConfigQuery.data, serverContextPath, catalogLayersQuery.data],
    );
    // Výchozí stav filtru = nic zaškrtnuté = nefiltruje se (viz efekt níže).
    const filterTree = useFilterTree(filterTreeNodes);
    // Všechny layerId vrstev ve filtru (LAYER targety) – pro řízení viditelnosti (filtr = katalog).
    const allFilterLayerIds = useMemo(() => collectLayerTargetIds(filterTreeNodes), [filterTreeNodes]);
    // Stabilní klíč výběru – spouštěč aplikačního efektu (Set nemá stabilní identitu napříč rendery).
    const checkedKey = useMemo(
        () =>
            Array.from(filterTree.checkedIds)
                .sort((a, b) => a - b)
                .join(','),
        [filterTree.checkedIds],
    );
    // Aby se na mountu (nic vybráno) nepřepsal existující filtr mapy – aplikujeme až po první reálné změně.
    const hasAppliedFilterRef = useRef(false);
    // Dokud uživatel ve filtru nic nezaškrtl, filtr do mapy vůbec NEZASAHUJE (viditelnost vrstev zůstává
    // podle katalogu/tématu). Jakmile jednou něco zaškrtnul, je i pozdější prázdný výběr (vyklikané tagy /
    // „Vynulovat filtr") jeho záměrem = nefiltrovat a zobrazit vše.
    const filterTouchedRef = useRef(false);

    // Zaškrtnuté targety → filtr mapového enginu. setFilter přefiltruje mapu a re-aktivací extentu si vyžádáme
    // přepočet objektů ve výřezu (engine pošle už odfiltrované pointery přes stávající EXTENT_FEATURES pipeline).
    useEffect(() => {
        if (!topic?.publicSimpleView) return;
        if (filterTree.checkedIds.size > 0) filterTouchedRef.current = true;
        if (!filterTouchedRef.current) return;
        const {layerConditions, layerIds} = buildAppliedFilter(filterTree.getCheckedTargets());

        // 1) Viditelnost vrstev (filtr = katalog): zachovej ne-filtrové aktivní vrstvy (pozadí…) a filtrové
        //    nastav podle zaškrtnutí. Popisové (label) vrstvy zapínáme/vypínáme SPOLEČNĚ s jejich datovou
        //    vrstvou – stejně jako v CatalogLayerTreeList (getLabelLayerIds). Aplikuj jen při reálné změně.
        const layersConfig = layersConfigQuery.data;
        const labelIdsByFilterLayer = new Map(allFilterLayerIds.map(id => [id, getLabelLayerIds(layersConfig, id)]));
        const checkedLayerIdSet = new Set(layerIds.length > 0 ? layerIds : allFilterLayerIds);
        // Filtrem „spravované" vrstvy = filtrové datové vrstvy + jejich popisové vrstvy.
        const managedLayerIdSet = new Set<number>([...allFilterLayerIds, ...Array.from(labelIdsByFilterLayer.values()).flat()]);
        const checkedLabelIds = allFilterLayerIds
            .filter(id => checkedLayerIdSet.has(id))
            .flatMap(id => labelIdsByFilterLayer.get(id) ?? []);
        const currentLayers = activeLayers.map(Number);
        const nextLayers = Array.from(
            new Set<number>([
                ...currentLayers.filter(id => !managedLayerIdSet.has(id)),
                ...allFilterLayerIds.filter(id => checkedLayerIdSet.has(id)),
                ...checkedLabelIds,
            ]),
        );
        const layersChanged = nextLayers.length !== currentLayers.length || nextLayers.some((id, i) => id !== currentLayers[i]);
        if (layersChanged) {
            mapRef?.current?.setLayers(nextLayers);
            setActiveLayers(nextLayers as LayerId[]);
        }

        // 2) Atributové podmínky → engine filter. Na mountu (nic vybráno) nepřepisujeme existující filtr mapy.
        if (hasAppliedFilterRef.current || layerConditions.length > 0) {
            hasAppliedFilterRef.current = true;
            // setFilter nahrazuje CELÝ FeatureFilter → mergneme s aktuálním (datumovým) filtrem mapy.
            const base = dateFilterRange ?? {};
            mapRef?.current?.setFilter({...base, layerConditions: layerConditions.length ? layerConditions : undefined});
        }

        // 3) Přepočet objektů ve výřezu (po změně vrstev i podmínek).
        extentRefreshRef.current?.();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checkedKey, dateFilterRange, topic?.publicSimpleView, layersConfigQuery.data]);

    // Otevření filtru: uložíme snímek aktuálního výběru, ať ho lze při „zrušit změny" obnovit.
    const openFilter = useCallback(() => {
        filterSnapshotRef.current = new Set(filterTree.checkedIds);
        setFilterMode(true);
    }, [filterTree.checkedIds]);

    // Potvrzení (mcCheck): změny se aplikují průběžně (viz efekt níže), stačí zavřít filtr.
    const confirmFilter = useCallback(() => setFilterMode(false), []);

    // Zrušení (křížek): obnovíme výběr ze snímku a zavřeme filtr.
    const cancelFilter = useCallback(() => {
        if (filterSnapshotRef.current) filterTree.replaceChecked(filterSnapshotRef.current);
        setFilterMode(false);
    }, [filterTree]);

    const registerBackToList = useCallback(() => {
        onItemSelected(() => setSelectedDetailFeature([]));
    }, [onItemSelected, setSelectedDetailFeature]);

    const search = useSearchPanelSearch({
        onFeatureSelected: registerBackToList,
        // Klik na ulici/lokaci: vynulování filtru (odškrtne vše = zobraz vše) a na mobilu sbal sheet, ať je vidět mapa.
        onLocationSelected: () => {
            filterTree.clearAll();
            if (mobileSheet) setCollapseSignal(s => s + 1);
        },
    });
    // Výřezový režim (extent + seznam) běží jen když je panel aktivní a není odeslané hledání (až po "Vyhledat").
    const viewportMode = active && !search.isSearchActive;
    const viewport = useViewportFeatures({extentFeaturePointers, enabled: viewportMode});
    // Aktivace extent filtru (setLayerFeaturesFilter) – celá uvnitř panelu; MapPage jen spouští refresh při pohybu mapou.
    useSearchPanelExtent(extentRefreshRef, viewportMode);

    const showSearchResults = search.isSearchActive;

    // Rozdělení výsledků hledání: OBJEKTY (POI, mají featureId) → bohaté karty; ULICE/adresy (LOCATION) → řádky
    // pod objekty; aktivace VRSTVY (LAYER bez featureId) → prosté řádky na konci. Skupinové title řádky se
    // v tomto zobrazení nepoužívají.
    const isDataRow = (i: SearchListItem): boolean => !i.statusRow && !i.title;
    const poiResults = useMemo(() => search.items.filter(i => isDataRow(i) && !!i.attrs?.featureId), [search.items]);
    const streetResults = useMemo(
        () => search.items.filter(i => isDataRow(i) && !i.attrs?.featureId && i.typVysledek === EnumTypVysledek.LOCATION),
        [search.items],
    );
    const layerResults = useMemo(
        () => search.items.filter(i => isDataRow(i) && !i.attrs?.featureId && i.typVysledek === EnumTypVysledek.LAYER),
        [search.items],
    );
    const poiPointers = useMemo<FeaturePointerVO[]>(
        () => poiResults.map(i => ({featureId: i.attrs!.featureId!, layerId: Number(i.attrs!.layer)})),
        [poiResults],
    );
    const {cardsById} = useSearchResultCards(poiPointers);

    const handleViewportClick = useCallback(
        (item: ViewportResult) => {
            const featurePointer = {featureId: item.featureId, layerId: item.layerId as LayerId};
            // Nejdřív zhasnout případné hover zvýraznění (na dotyku mouseleave nemusí přijít), pak vybrat.
            mapRef?.current?.highlightFeature(null, true);
            mapRef?.current?.selectFeatures([featurePointer], {zoom: FeatureZoom.CENTER});
            setSelectedDetailFeature([featurePointer]);
            registerBackToList();
        },
        [mapRef, setSelectedDetailFeature, registerBackToList],
    );

    // Hover nad položkou: pouze zvýraznit objekt v mapě (NECENTROVAT – centrování se chovalo divně).
    const handleViewportHover = useCallback(
        (item: ViewportResult) => {
            mapRef?.current?.highlightFeature({featureId: item.featureId, layerId: item.layerId as LayerId}, true);
        },
        [mapRef],
    );

    const handleViewportLeave = useCallback(() => {
        // Zrušení hover zvýraznění: null + hover=true. Předání pointeru s hover=false naopak vytvoří
        // TRVALÝ highlight (větev pro klik v enginu), který v mapě zůstane viset – proto se dřív poslední
        // najetý objekt „nezhasl".
        mapRef?.current?.highlightFeature(null, true);
    }, [mapRef]);

    const handleOpenLegend = async (): Promise<void> => {
        const {lang, context} = mapioContext.value;
        // Přednačteme všechna data legendy (atributy pro vyhledávání, konfiguraci vrstev i legendy WMS/WMTS vrstev)
        // jedním Promise.all, aby se legenda otevřela rovnou s daty – shodně s katalogem vrstev.
        const {searchableAttributes, layersConfig, layerLegends} = await Modal.showWaiting(
            actionApiCall(async () => await preloadLegendData(activeLayers as LayerId[], lang, context, layersConfigQuery.data)),
            () => <ApiCallWaitingModal onClose={undefined} />,
        );

        Modal.show(({onClose}) => (
            <LegendModal
                onClose={onClose}
                layerIds={activeLayers}
                searchableAttributes={searchableAttributes}
                layersConfig={layersConfig}
                layerLegends={layerLegends}
                formattedLegend={topic?.formatovanaLegenda}
            />
        ));
    };

    if (!topic?.publicSimpleView) return null;

    /** Řádek výsledku hledání (ulice, aktivace vrstvy) – prostý řádek. */
    const renderSearchRow = (item: SearchListItem, key: string): React.JSX.Element => (
        <SearchResultRow
            key={key}
            item={item}
            searchText={search.searchText}
            layersConfig={layersConfigQuery.data}
            serverContextPath={serverContextPath}
            onClick={search.onResultClick}
            onMouseEnter={search.onResultHover}
            onMouseLeave={search.onResultLeave}
        />
    );

    // Výsledky hledání: nejdřív OBJEKTY jako bohaté karty (stejně jako výřez), pod nimi ULICE, pak aktivace vrstev.
    const searchResultsSection = (
        <>
            {search.isSearching && (
                <div className={'p-2 text-center'}>
                    <Text intent={'secondary'}>Vyhledávám...</Text>
                </div>
            )}
            {!search.isSearching && search.items.length === 0 && (
                <div className={'p-2'}>
                    <Text intent={'secondary'}>Pro zadaný výraz nebyly nalezeny žádné záznamy.</Text>
                </div>
            )}
            {(poiResults.length > 0 || streetResults.length > 0) && (
                <div className={'search-status-row px-1 py-1'}>
                    <Text italic intent={'secondary'}>
                        {getFoundCountLabel(poiResults.length + streetResults.length)}
                    </Text>
                </div>
            )}
            {poiResults.length > 0 && (
                <div className={'search-cards'}>
                    {poiResults.map((item, index) => {
                        const id = `${item.attrs?.layer}:${item.attrs?.featureId}`;
                        const card = cardsById[id];
                        // Dokud se karta nedotáhne, zobrazíme prostý řádek (pak se povýší na kartu).
                        return card ? (
                            <div key={`${id}-${index}`} className={'viewport-virtual-row'}>
                                <ViewportResultItem
                                    item={card}
                                    onClick={handleViewportClick}
                                    onMouseEnter={handleViewportHover}
                                    onMouseLeave={handleViewportLeave}
                                />
                            </div>
                        ) : (
                            renderSearchRow(item, `poi-${id}-${index}`)
                        );
                    })}
                </div>
            )}
            {/* Vyhledané ulice VŽDY pod nalezenými objekty. */}
            {streetResults.map((item, index) => renderSearchRow(item, `street-${item.attrs?.layer}-${item.attrs?.featureId ?? item.id}-${index}`))}
            {layerResults.map((item, index) => renderSearchRow(item, `layer-${item.attrs?.layer}-${item.id}-${index}`))}
        </>
    );

    // Režim výřezu – objekty v aktuálním výřezu mapy (bohaté položky, virtualizované).
    const viewportSection = (
        <>
            {(extentFeaturePointers === undefined || !viewport.isReady) && (
                <div className={'p-2 text-center'}>
                    <Text intent={'secondary'}>Načítám objekty ve výřezu...</Text>
                </div>
            )}
            {extentFeaturePointers !== undefined && viewport.isReady && viewport.ids.length === 0 && (
                <div className={'p-2'}>
                    <Text intent={'secondary'} italic>
                        Žádné objekty ve výřezu mapy.
                    </Text>
                </div>
            )}
            {extentFeaturePointers !== undefined && viewport.isReady && viewport.ids.length > 0 && (
                <ViewportVirtualList
                    className={'viewport-virtual-list'}
                    rowClassName={'viewport-virtual-row'}
                    ids={viewport.ids}
                    api={viewport.api}
                    defaultRowHeight={VIEWPORT_ROW_HEIGHT}
                    renderLoading={() => (
                        <ListItem className={'viewport-result-item viewport-result-loading'}>
                            <div className={'result-image-placeholder'} />
                        </ListItem>
                    )}
                >
                    {row => <ViewportResultItem item={row} onClick={handleViewportClick} onMouseEnter={handleViewportHover} onMouseLeave={handleViewportLeave} />}
                </ViewportVirtualList>
            )}
        </>
    );

    const listModeContent = (
        <>
            <div /*className={'mb-2'}*/>
                <SearchPanelInput
                    value={search.searchText}
                    disabled={disabled}
                    onChange={search.onTextChange}
                    onSearch={search.onSubmit}
                    onClear={search.onClear}
                    // Mobil: focus vyhledávacího pole vytáhne bottom sheet z peeku (12 vh) na 50 vh.
                    onFocus={
                        mobileSheet
                            ? () => {
                                  setExpandTargetIndex(SHEET_SEARCH_SNAP_INDEX);
                                  setExpandSignal(s => s + 1);
                              }
                            : undefined
                    }
                />
            </div>
            {/* Lišta filtru (tlačítko Filtrovat + zafiltrované tagy) – v seznamu i ve výsledcích hledání. */}
            <FilterTagsBar tree={filterTreeNodes} filterTree={filterTree} onOpenSettings={openFilter} />
            <div className={'search-panel-results'}>{showSearchResults ? searchResultsSection : viewportSection}</div>
        </>
    );

    const panelBody = (
        <div className={'search-panel-body px-2'}>{filterMode ? <FilterTreePanel tree={filterTreeNodes} filterTree={filterTree} /> : listModeContent}</div>
    );

    const legendButton = (
        <Button onClick={handleOpenLegend} style={'link'}>
            Legenda
        </Button>
    );

    // Mobil (veřejné Mapio): trvalý bottom sheet – seznam je vždy, když není vybrán objekt, a otevírá se
    // v poloviční poloze (50 vh). Legenda jen když je vytažen úplně nahoru. Filtr se otevírá přes celou
    // obrazovku jako samostatný overlay (viz níže) – v sheetu proto renderujeme VŽDY jen seznam.
    if (mobileSheet) {
        return (
            <>
                <BottomSheet
                    open={active}
                    snapPoints={SHEET_SNAP_POINTS}
                    defaultSnapPointIndex={0}
                    hideClose
                    dismissibleByDrag={false}
                    collapseSignal={collapseSignal}
                    expandSignal={expandSignal}
                    expandSnapPointIndex={expandTargetIndex}
                    onSnapChange={vh => setSheetAtMax(vh >= MAX_SNAP_VH)}
                    className={'search-panel-sidebar search-panel-sheet'}
                    handleBackgroundColor="#F5F7FA"
                >
                    <div className={'search-panel-body p-2'}>{listModeContent}</div>
                    {sheetAtMax && <div className={'search-panel-sheet-legend d-flex justify-content-center flex-shrink-0 py-1'}>{legendButton}</div>}
                </BottomSheet>
                {/* Nastavení filtru přes celou obrazovku: křížek vlevo zruší provedené změny, mcCheck vpravo potvrdí. */}
                {filterMode && (
                    <div className={'filter-fullscreen-mobile'}>
                        <FilterTreePanel tree={filterTreeNodes} filterTree={filterTree} onCancel={cancelFilter} onConfirm={confirmFilter} />
                    </div>
                )}
            </>
        );
    }

    // Desktop: klasická karta v sidebaru. Panel zůstává namountovaný a při neaktivitě se jen skryje,
    // aby po "Zpět na seznam" zůstal zachován stav hledání.
    return (
        <SidebarContent
            title={
                filterMode ? (
                    <Button style={'link'} icon={<Icon icon={mcMoveLeft} />} onClick={() => setFilterMode(false)}>
                        Zpět na seznam
                    </Button>
                ) : (
                    'Hledání'
                )
            }
            icon={filterMode ? null : mcSearch}
            iconIntent="default"
            onClose={onClose}
            className={'search-panel-sidebar'}
            style={active ? {} : {display: 'none'}}
        >
            {panelBody}
            <div className="search-panel-footer d-flex justify-content-end pr-2 py-1">{legendButton}</div>
        </SidebarContent>
    );
};
